import { BlockCategory } from './block-connection-categories'
import { Role } from './companies'
import { EmploymentType } from './employmentType'
import { Worker } from './workers'

export enum ConnectionType {
  BLOCK = 'BLOCK',
  FAVORITE = 'FAVORITE',
  INELIGIBLE = 'INELIGIBLE',
  SCHEDULED_BLOCK = 'SCHEDULED_BLOCK',
  TEMP_TO_HIRE = 'TEMP_TO_HIRE',
  VETTED = 'VETTED',
  SOURCED = 'SOURCED',
}

export enum EligibilityConnectionType {
  INELIGIBLE = 'INELIGIBLE',
  ELIGIBLE = 'ELIGIBLE',
}

export enum EligibilityConnectionLevel {
  ROLE = 'ROLE',
  LOCATION = 'LOCATION',
  ROLE_AND_LOCATION = 'ROLE_AND_LOCATION',
}

export type EligibilityConnection = {
  roleId?: string
  locationId?: string
  eligibilityConnectionType: EligibilityConnectionType
  eligibilityConnectionLevel: EligibilityConnectionLevel
}

export type EligibilityConnectionsByType = {
  roles: EligibilityConnection[]
  locations: EligibilityConnection[]
  locationAndRole: EligibilityConnection[]
}

export type GlobalEligibilityConnections = {
  globalEligibilityForLocations?: EligibilityConnectionType
  globalEligibilityForRoles?: EligibilityConnectionType
}

export type GlobalEligibilityConnectionsPayload = {
  globalEligibilityForLocations: EligibilityConnectionType
  globalEligibilityForRoles: EligibilityConnectionType
}

export type EligibilityConnectionResponse = {
  eligibilityConnections: EligibilityConnectionsByType
  globalEligibilities: GlobalEligibilityConnections
}

export type EligibilityConnectionMutationPayload = {
  workerId: string
  companyId: string
  initiatedBy: string
  eligibilityConnections: EligibilityConnectionsByType
  globalEligibilities: GlobalEligibilityConnectionsPayload
}

export enum ConnectionSource {
  COMPANY = 'COMPANY',
  WORKER = 'WORKER',
}

// Keep this up to date with the BE KnownBlockLabels
// that are checked when connectionReason/connectionReasons are saved
// If you add new ones, make them keys
export enum ConnectionReason {
  // Used in Biz App
  UNABLE_TO_PERFORM_REQUIREMENTS = 'UNABLE_TO_PERFORM_REQUIREMENTS',
  UNPROFESSIONAL_UNCOOPERATIVE = 'UNPROFESSIONAL_UNCOOPERATIVE',
  SERIOUS_INCIDENT = 'SERIOUS_INCIDENT',
  LOW_PRODUCTIVITY = 'LOW_PRODUCTIVITY',
  // Used in Ops App
  POLICY_VIOLATION = 'POLICY_VIOLATION',
  HOSTILE_INCIDENT = 'HOSTILE_INCIDENT',
  CONSTANT_BAD_PERFORMANCE_PRODUCTIVITY = 'CONSTANT_BAD_PERFORMANCE_PRODUCTIVITY',
  CONSISTENTLY_LATE = 'CONSISTENTLY_LATE',
  CONSISTENTLY_ABSENT = 'CONSISTENTLY_ABSENT',
  NO_CALL_NO_SHOW = 'NO_CALL_NO_SHOW',
  ABANDONED_SHIFT = 'ABANDONED_SHIFT',
  DOES_NOT_FOLLOW_INSTRUCTIONS = 'DOES_NOT_FOLLOW_INSTRUCTIONS',
  DISRUPTIVE_OR_INAPPROPRIATE_BEHAVIOR = 'DISRUPTIVE_OR_INAPPROPRIATE_BEHAVIOR',
  TAKES_EXCESSIVE_BREAKS = 'TAKES_EXCESSIVE_BREAKS',

  // Ineligible Reasons
  SOFT_SKILLS = 'SOFT_SKILLS', // communication, attention to detail
  HARD_SKILLS = 'HARD_SKILLS', // weight lifting, machine operation
  POLICY_VIOLATIONS = 'POLICY_VIOLATIONS', // unsafe, drug use
  LANGUAGE_REQUIREMENT = 'LANGUAGE_REQUIREMENT',
  // CONSISTENTLY_LATE = 'CONSISTENTLY_LATE',
  // DOES_NOT_FOLLOW_INSTRUCTIONS = 'DOES_NOT_FOLLOW_INSTRUCTIONS',
  // CONSISTENTLY_ABSENT = 'CONSISTENTLY_ABSENT',
  INAPPROPRIATE_BEHAVIOR = 'INAPPROPRIATE_BEHAVIOR',
  OTHER = 'OTHER',

  /** @deprecated reasons below **/
  // DEPRECATED_DOES_NOT_MEET_SKILL_REQUIREMENTS = 'DOES_NOT_MEET_SKILL_REQUIREMENTS',
  // DEPRECATED_TOO_MANY_ABSENCES = 'TOO_MANY_ABSENCES',
  // DEPRECATED_DISTRACTED_OR_AVOIDS_WORK = 'DISTRACTED_OR_AVOIDS_WORK',
  // DEPRECATED_SAFETY_INCIDENT = 'SAFETY_INCIDENT',
  // DEPRECATED_TAKES_EXCESSIVELY_LONG_BREAKS = 'TAKES_EXCESSIVELY_LONG_BREAKS',
  // DEPRECATED_UNDER_THE_INFLUENCE = 'UNDER_THE_INFLUENCE',
  // DEPRECATED_CONNECTION_REASON_OTHER = 'CONNECTION_REASON_OTHER',
  // DEPRECATED_DOES_NOT_FOLLOW_INSTRUCTIONS = 'Does not follow instructions', // updated to be a key format
  // DEPRECATED_ABANDONED_SHIFT = 'Abandoned shift', // updated to be a key format
  // DEPRECATED_UNDER_THE_INFLUENCE = 'Under the influence', // updated to be a key format
  // DEPRECATED_CONSISTENTLY_LATE = 'Consistently late', // updated to be a key format
  // DEPRECATED_CANNOT_PERFORM_REQUIRED_TASK = 'Cannot perform required task', // replaced by DOES_NOT_MEET_SKILL_REQUIREMENTS
  // DEPRECATED_DOES_NOT_WANT_TO_WORK = 'Does not want to work', // replaced by DISTRACTED_OR_AVOIDS_WORK
  // DEPRECATED_WORKER_ACTIVELY_AVOIDS_WORKING = 'Worker actively avoids working', // replaced by DISTRACTED_OR_AVOIDS_WORK
  // DEPRECATED_CANNOT_WORK_WELL_WITH_OTHERS = 'Cannot work well with others', // replaced by DISRUPTIVE_OR_INAPPROPRIATE_BEHAVIOR
  // DEPRECATED_WORKS_TOO_SLOWLY = 'Works too slowly',
  // DEPRECATED_CREATES_DISRUPTIVE_WORK_ENVIRONMENT = 'Creates disruptive work environment', // replaced by DISRUPTIVE_OR_INAPPROPRIATE_BEHAVIOR
  // DEPRECATED_ABUSIVE_BEHAVIOR = 'Abusive behavior', // replaced by DISRUPTIVE_OR_INAPPROPRIATE_BEHAVIOR
  // DEPRECATED_NO_SHOW_AT_SHIFT = 'No Show at Shift', // replaced by NO_CALL_NO_SHOW
}

// These are the text representations for the dropdown in the Ops App
// When they are blocking a worker
// (biz app sets up their text differently)
export const BLOCK_REASON_TO_TEXT_FOR_OPS: Partial<
  Record<ConnectionReason, string>
> = {
  [ConnectionReason.POLICY_VIOLATION]: 'Policy violation (unsafe, drug use)',
  [ConnectionReason.HOSTILE_INCIDENT]: 'Hostile incident',
  [ConnectionReason.CONSTANT_BAD_PERFORMANCE_PRODUCTIVITY]:
    'Constant bad performance / productivity',
  [ConnectionReason.CONSISTENTLY_LATE]: 'Constant late arrival',
  [ConnectionReason.CONSISTENTLY_ABSENT]: 'Constant bad attendance',
  [ConnectionReason.NO_CALL_NO_SHOW]: 'No call no show',
  [ConnectionReason.ABANDONED_SHIFT]: 'Abandoned shift',
  [ConnectionReason.DOES_NOT_FOLLOW_INSTRUCTIONS]:
    'Does not follow instructions',
  [ConnectionReason.DISRUPTIVE_OR_INAPPROPRIATE_BEHAVIOR]:
    'Inappropriate behavior',
  [ConnectionReason.TAKES_EXCESSIVE_BREAKS]: 'Takes excessive breaks',
}

export const INELIGIBLE_REASON_TO_TEXT_FOR_OPS: Partial<
  Record<ConnectionReason, string>
> = {
  [ConnectionReason.SOFT_SKILLS]:
    'Soft skills (communication, attention to detail)',
  [ConnectionReason.HARD_SKILLS]:
    'Hard skills (weight lifting, machine operation)',
  [ConnectionReason.POLICY_VIOLATIONS]: 'Policy violations (unsafe, drug use)',
  [ConnectionReason.LANGUAGE_REQUIREMENT]: 'Language requirement',
  [ConnectionReason.DOES_NOT_FOLLOW_INSTRUCTIONS]:
    'Does not follow instructions',
  [ConnectionReason.INAPPROPRIATE_BEHAVIOR]: 'Inappropriate behavior',
  [ConnectionReason.CONSISTENTLY_LATE]: 'Constant late arrival',
  [ConnectionReason.CONSISTENTLY_ABSENT]: 'Constant bad attendance',
  [ConnectionReason.OTHER]: 'Other',
}

export type WorkerConnections = {
  blocked: string[]
  ineligible: string[]
  workerFavorites: string[]
  companyFavorites: string[]
}

/** Uploads linked to a connection (e.g. block policy artifacts). */
export type ConnectionLinkedWorkerUpload = {
  id: string
  url: string
  title: string
  mimeType: string
}

export type Connection = {
  id: string
  companyId: string
  connectionType: ConnectionType
  createdAt: string
  source: ConnectionSource
  workerId: string
  initiatedBy?: string
  note?: string
  connectionReason?: ConnectionReason
  connectionReasons?: ConnectionReason[]
  connectionReasonCategories?: BlockCategory[]
  updatedAt: string
  roles?: RoleConnection[]
  linkedWorkerUploads?: ConnectionLinkedWorkerUpload[]
}

export type RoleConnection = {
  id: string
  createdAt: Date
  initiatedBy?: string
  role: Pick<Role, 'roleId' | 'roleName' | 'activeRoleId'>
  note?: string
}

export type WorkerConnectionResponse = Connection & {
  employerName: string
}

export type CompanyConnectionResponse = Connection & {
  worker: Worker
}

export type BaseConnection = {
  connectionType: ConnectionType
}

export type CompanyConnectionDto = BaseConnection & {
  workerId: string
}

export type WorkerConnectionDto = BaseConnection & {
  companyId: string
}

export type CompanyConnectionOnBehalfDto = CompanyConnectionDto & {
  initiatedBy: string
  note?: string
  bypassRestrictions?: boolean
  /**
   * For SOURCED connections only: when > 0, recompute charges for this worker's
   * shifts on the company's most recent N invoices. Defaults to no resync.
   */
  resyncRecentInvoicesCount?: number
}

export type ConnectionDto = Connection & {
  bypassRestrictions?: boolean
}

export type ConnectionQueryDto = {
  companyId: string
  workerId?: string
  connectionType?: ConnectionType
  source?: ConnectionSource
}

export type CreateConnectionResponse = {
  connection: Connection | null
  skipped: boolean
  existingConnectionInitiatedBy?: string | null
}

/**
 * Lifecycle of a block connection review handled in the Termination Action
 * Center. Mirrors `ConnectionReviewStatus` on the backend; we re-declare it
 * in shared/types so frontend code does not import from the NestJS app.
 */
export enum ConnectionReviewStatus {
  PENDING = 'PENDING',
  ESCALATED = 'ESCALATED',
  DISMISSED = 'DISMISSED',
  TERMINATED = 'TERMINATED',
}

/**
 * Row shape returned by `GET /connections/review/terminations` and the
 * action endpoints (`/escalate`, `/dismiss`, `/reopen`, `/terminate`).
 */
export interface ConnectionReviewItem {
  connectionId: string
  worker: {
    id: string
    firstName: string
    lastName: string
    /**
     * Worker's email. Surfaced on the list response so the TerminationModal
     * opened from the Termination Action Center can prefill the recipient
     * field — without it the modal renders a blank recipient. Nullable for
     * the rare legacy worker rows with a null email; the FE falls back to a
     * blank input in that case.
     */
    email: string | null
  }
  company: { id: string; name: string }
  region: { id: string; displayName: string } | null
  reviewStatus: ConnectionReviewStatus
  connectionReason: string | null
  connectionReasonCategories: BlockCategory[]
  createdAt: string
  lastReviewedBy: string | null
  lastReviewedAt: string | null
  terminatedBy: string | null
  terminatedAt: string | null
  hasOpenIncidentOrClaim: boolean
  /**
   * Worker's current employment classification. Surfaced on the list
   * response so the TerminationModal can decide whether to enable Submit —
   * the W2 termination flow only works for W2 workers.
   */
  employmentType: EmploymentType | null
  /**
   * Worker's Zeal employee record id, when present. Required for the W2
   * termination flow; absent when W2 onboarding never completed. Surfaced
   * on the list response so the modal's eligibility check can read it
   * without an extra fetch.
   */
  zealEmployeeId: string | null
}
